import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import cn from 'classnames'
import Switch from 'react-switch'
import moment from 'moment'

import { hideDocumentForm } from '../../../store/documents/actions'
import { createDocument, getDocumentTypes, getCompanyEmployeesAll, getPlaces, getDocument, updateDocument } from '../../../api'
import { generateResourcesFromEmployees, getCheckedEmployeeIds, updateAllEmployees, setCheckedEmployees } from '../../TimeOff/Calendar/utils'
import useNotify from '../../../hooks/useNotify'
import { useQuery } from '../../../hooks/useQuery'

import styles from './styles.module.scss'

import { CrossIcon } from '../Icons'
import Input from '../../Core/Input/Input'
import InputSelect from '../../Core/InputSelect'
import Label from '../../Core/InputLabel'
import CustomSelect from '../../Core/CustomSelect'


const ASSIGN_TO_OPTIONS = [
  { id: 'one_employee', title: 'One employee' },
  { id: 'selected_employees', title: 'Selected employees' },
  { id: 'place', title: 'Place' },
  { id: 'company', title: 'All active employees' },
]

const initialForm = {
  document_type_id: null,
  name: '',
  effective_date: null,
  expiration_date: null,
  due_date_for_action: null,
  require_acknowledgment: false,
  track_reading_time: false,
  require_approval: false,
  require_signature: false,
  employee_can_view: true,
  reminder: false,
  notes: '',
  documents: [],
  assignment_type: 'one_employee',
  assignments: [],
}

const buildDocumentFormData = (data) => {
  const formData = new FormData();

  // Document fields
  formData.append(
    'document_type_id',
    String(data.document_type_id)
  );

  formData.append('name', data.name);
  formData.append('assignment_type', data.assignment_type)

  const fields = [
    'effective_date',
    'expiration_date',
    'due_date_for_action',
    'notes',
    'status',
  ];

  fields.forEach((field) => {
    if (data[field] !== null && data[field] !== undefined) {
      formData.append(field, String(data[field]));
    }
  });

  const booleanFields = [
    'require_acknowledgment',
    'track_reading_time',
    'require_approval',
    'require_signature',
    'employee_can_view',
    'reminder',
  ];

  booleanFields.forEach((field) => {
    formData.append(
      field,
      data[field] ? '1' : '0'
    );
  });

  // Assignments
  data.assignments.forEach((assignment, index) => {
    if (assignment.employee_id !== null &&
        assignment.employee_id !== undefined) {
      formData.append(
        `assignments[${index}][employee_id]`,
        String(assignment.employee_id)
      );
    }

    if (assignment.place_id !== null &&
        assignment.place_id !== undefined) {
      formData.append(
        `assignments[${index}][place_id]`,
        String(assignment.place_id)
      );
    }
  });

  // Files
  data.documents.forEach((file, index) => {
    formData.append(
      `documents[${index}][file]`,
      file
    );
  });

  return formData;
}

const DocumentForm = ({companyId}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { employee_id: employeeId } = useQuery(['employee_id'])

  const { show, data } = useSelector(state => state.documents.form)
  const { show: showNotify } = useNotify()
  
  const [{documentTypes, employees, places}, setOptions] = useState({
    documentTypes: [],
    employees: [],
    places: []
  })
  const [showLayout, setShowLayout] = useState(false)
  const [form, setForm] = useState(initialForm)
  const [errors, setErrors] = useState({})
  const [dragActive, setDragActive] = useState(false)

  const fileInputRef = useRef(null)

  const isEdit = Boolean(data?.documentId)

  const selectedDocumentType = documentTypes.find(dt => dt.id === form.document_type_id) || {}
  const requiresAction = form.require_acknowledgment || form.require_approval || form.require_signature

  const canUpload = selectedDocumentType.ownership === 'company' || (selectedDocumentType.ownership === 'employee' && form.assignment_type === 'one_employee')

  useEffect(() => {
    if (show) {
      setForm(initialForm)
      setErrors({})
      setTimeout(() => {
        setShowLayout(true)
      }, 100)
    } else {
      setShowLayout(false)
    }
  }, [show, data?.documentId, employeeId])

  useEffect(() => {
    if (companyId && show) {
      init(companyId, data?.documentId)
    }
  }, [companyId, show, data?.documentId])

  useEffect(() => {
    if (selectedDocumentType.id && !data?.documentId) {
      setForm(prev => ({
        ...prev,
        require_acknowledgment: prev.require_acknowledgment || selectedDocumentType?.require_acknowledgment,
        track_reading_time: prev.track_reading_time || selectedDocumentType?.track_reading_time,
        require_approval: prev.require_approval || selectedDocumentType?.require_approval,
        require_signature: prev.require_signature || selectedDocumentType?.require_signature,
      }))
    }
  }, [selectedDocumentType.id, data?.documentId])

  const init = async (companyId, documentId) => {
    const [documentTypesRes, employeesRes, placesRes, documentRes] = await Promise.all([
      getDocumentTypes(companyId),
      getCompanyEmployeesAll(companyId),
      getPlaces(companyId),
      documentId ? getDocument(companyId, documentId) : null
    ])
    let employees = employeesRes?.success ? generateResourcesFromEmployees(employeesRes?.users) : []
    if (documentRes?.data) {
      let assignments = []
      if (documentRes.data.assignment_type === 'employee') {
        employees = setCheckedEmployees(employees, documentRes.data.employees.map(item => item.employee_id), true)
        assignments = documentRes.data.employees.map(item => ({employee_id: item.employee_id}))
      }
      if (documentRes.data.assignment_type === 'place') {
        assignments = [{place_id: documentRes.data.place_id}]
      }
      setForm(prev => {
        return {
          ...prev,
          document_type_id: documentRes.data.document_type_id,
          name: documentRes.data.name,
          effective_date: documentRes.data.effective_date ? moment(documentRes.data.effective_date).format('YYYY-MM-DD') : null,
          expiration_date: documentRes.data.expiration_date ? moment(documentRes.data.expiration_date).format('YYYY-MM-DD') : null,
          due_date_for_action: documentRes.data.due_date_for_action ? moment(documentRes.data.due_date_for_action).format('YYYY-MM-DD') : null,
          require_acknowledgment: documentRes.data.require_acknowledgment,
          track_reading_time: documentRes.data.track_reading_time,
          require_approval: documentRes.data.require_approval,
          require_signature: documentRes.data.require_signature,
          employee_can_view: documentRes.data.employee_can_view,
          reminder: documentRes.data.reminder,
          notes: documentRes.data.notes,
          assignment_type: documentRes.data.assignment_type === 'employee' ? (assignments.length ? 'selected_employees' : 'one_employee') : documentRes.data.assignment_type,
          assignments: assignments,
        }
      })
    }
    setOptions({
      documentTypes: documentTypesRes?.success ? documentTypesRes.data : [],
      employees: employeeId ? setCheckedEmployees(employees, [employeeId], true) : employees,
      places: Array.isArray(placesRes) ? placesRes.map(p => ({id: p.id, name: p.name})) : []
    })
    setForm(prev => ({...prev, document_type_id: prev.document_type_id || documentTypesRes?.data?.[0]?.id}))
  }

  const handleClose = () => {
    dispatch(hideDocumentForm())
  }

  const handleChange = (name) => (e) => {
    const { value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleToggle = (name) => (checked) => {
    setForm(prev => ({ ...prev, [name]: checked }))
  }

  const handleSelectFiles = (selectedFiles) => {
    const filesArray = Array.from(selectedFiles || [])
    if (!filesArray.length) return
    setForm(prev => ({ ...prev, documents: [...prev.documents, ...filesArray] }))
    setErrors(prev => ({ ...prev, documents: null }))
  }

  const handleFileInputChange = (e) => {
    handleSelectFiles(e.target.files)
    e.target.value = ''
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    handleSelectFiles(e.dataTransfer.files)
  }

  const handleRemoveFile = (index) => {
    setForm(prev => ({ ...prev, documents: prev.documents.filter((_, i) => i !== index) }))
  }

  const handleChangeDocumentType = (e) => {
    const { value } = e.target
    const documentTypeId = value ? Number(value) : null
    const documentType = documentTypes.find(dt => dt.id === documentTypeId)

    setForm(prev => ({
      ...prev,
      document_type_id: documentTypeId,
      require_acknowledgment: !!documentType?.require_acknowledgment,
      track_reading_time: !!documentType?.track_reading_time,
      require_approval: !!documentType?.require_approval,
      require_signature: !!documentType?.require_signature,
      employee_can_view: !!documentType?.employee_can_view,
    }))
  }

  const handleChangeAssignType = (assignmentType) => {
    if (assignmentType === 'one_employee') {
      setOptions(prev => ({ ...prev, employees: updateAllEmployees(prev.employees, false) }))
    }
    setForm(prev => ({ ...prev, assignment_type: assignmentType, assignments: [] }))
  }

  const handleChangeAssignmentTarget = (e) => {
    const { value } = e.target
    if (form.assignment_type === 'place') {
      setForm(prev => ({ ...prev, assignments: value ? [{ place_id: Number(value) }] : [] }))
    } else {
      setForm(prev => ({ ...prev, assignments: value ? [{ employee_id: Number(value) }] : [] }))
    }
    setErrors(prev => ({ ...prev, assignments: null }))
  }

  const validateForm = data => {
    const errors = {}
    if (!data.name) {
      errors.name = t('Name is required')
    }
    if (!data.document_type_id) {
      errors.document_type_id = t('Document type is required')
    }
    if (selectedDocumentType.ownership === 'company' && (!data.documents || !data.documents.length) && !isEdit) {
      errors.documents = t('At least one document must be uploaded')
    }
    if (data.assignment_type !== 'company' && (!data.assignments || !data.assignments.length)) {
      errors.assignments = t('At least one employee must be selected')
    }
    if (selectedDocumentType.effective_date && !data.effective_date && data.documents.length) {
      errors.effective_date = t('Effective date is required')
    }
    if (selectedDocumentType.expiration_date && !data.expiration_date && data.documents.length) {
      errors.expiration_date = t('Expiration date is required')
    }
    setErrors(errors)
    console.log(errors)
    return Object.keys(errors).length === 0
  }

  

  const handleSubmit = async () => {
    const post = {
      ...form,
      employee_can_view: selectedDocumentType.ownership === 'employee' ? true : form.employee_can_view,
      assignment_type: ['one_employee', 'selected_employees'].includes(form.assignment_type) ? 'employee' : form.assignment_type,
      ...(['selected_employees', 'one_employee'].includes(form.assignment_type) ? { assignments: getCheckedEmployeeIds(employees).map((id) => ({ employee_id: id })) } : null)
    }
    if (!validateForm(post)) {
      return
    }
    if (isEdit) {
      const updates = selectedDocumentType.ownership === 'employee' ? {
        assignment_type: post.assignment_type,
        assignments: post.assignments,
        document_type_id: post.document_type_id,
        employee_can_view: post.employee_can_view,
        name: post.name,
        notes: post.notes,
        reminder: post.reminder,
        require_acknowledgment: post.require_acknowledgment,
        require_approval: post.require_approval,
        require_signature: post.require_signature,
        track_reading_time: post.track_reading_time,
      } : post
      const res = await updateDocument(companyId, data.documentId, updates)
      if (res) {
        handleSuccess(t('Document updated successfully'))
      }
      return
    }
    const formData = buildDocumentFormData(post)
    const res = await createDocument(companyId, formData)
    if (res) {
      handleSuccess(t('Document uploaded successfully'))
    }
  }

  const handleSuccess = (message) => {
    showNotify(message)
    handleClose()
    window.dispatchEvent(
      new CustomEvent('documents-updated', {detail: {}})
    )
  }

  return (
    <>
      {
        show
          ? <div className={cn(styles.layout, {[styles.active]: showLayout})} onClick={handleClose} />
          : null
      }
      <div className={cn(styles.container, {[styles.active]: show})}>
        <div className={styles.header}>
          <div>
            <div className={styles.headerTitle}>{t('Upload Document')}</div>
            <div className={styles.headerDescription}>{t('Assign a document and define what employees must do')}</div>
          </div>
          <button type="button" className={styles.closeButton} onClick={handleClose}>
            <CrossIcon />
          </button>
        </div>
        <div className={styles.content}>
          {
            canUpload && !isEdit
              ? <div
                  className={cn(styles.dropzone, { [styles.dropzoneActive]: dragActive, [styles.dropzoneError]: errors.documents })}
                  onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                >
                  <div className={styles.dropzoneIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M12 16V4M12 4L7 9M12 4L17 9" stroke="#808F94" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M4 16V17.5C4 18.8807 5.11929 20 6.5 20H17.5C18.8807 20 20 18.8807 20 17.5V16" stroke="#808F94" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className={styles.dropzoneTitle}>{ t('Drop files here') }</div>
                  <div className={styles.dropzoneHint}>{ t('PDF, JPG or PNG up to 20 MB') }</div>
                  <div className={styles.dropzoneActions}>
                    <button type="button" className={styles.primaryButton} onClick={() => fileInputRef.current?.click()}>
                      { t('Choose files') }
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    multiple
                    className={styles.hiddenInput}
                    onChange={handleFileInputChange}
                  />
                </div>
              : null
          }
          {
            form.documents.length ? (
              <div className={styles.fileList}>
                {
                  form.documents.map((document, index) => (
                    <div key={`${document.name}-${index}`} className={styles.fileListItem}>
                      <div className={styles.fileListItemName}>{ document.name }</div>
                      <button type="button" className={styles.fileListItemRemove} onClick={() => handleRemoveFile(index)}>
                        <CrossIcon />
                      </button>
                    </div>
                  ))
                }
              </div>
            ) : null
          }

          <div className={styles.formItem}>
            <Label text={t('Document title')} htmlFor='name' />
            <Input
              id='name'
              name='name'
              type='text'
              placeholder={t('Enter document title')}
              value={form.name}
              onChange={handleChange('name')}
              error={errors.name}
              fullWidth
            />
          </div>

          <div className={styles.formItem}>
            <Label text={t('Document type')} htmlFor='document_type_id' />
            <InputSelect
              disabled={isEdit}
              id='document_type_id'
              name='document_type_id'
              placeholder={t('Select document type')}
              options={documentTypes}
              value={form.document_type_id}
              onChange={handleChangeDocumentType}
              valueKey='id'
              labelKey='name'
              error={errors.document_type_id}
            />
            <div className={styles.fieldHint}>{ t('Defaults come from the type. Changes here apply to this document only.') }</div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Assign to') }</div>
            <div className={styles.cardGrid}>
              {
                ASSIGN_TO_OPTIONS.map(option => (
                  <div
                    key={option.id}
                    className={cn(styles.card, { [styles.cardActive]: form.assignment_type === option.id })}
                    onClick={() => handleChangeAssignType(option.id)}
                  >
                    <div className={styles.cardTitle}>{ t(option.title) }</div>
                  </div>
                ))
              }
            </div>
            {
              form.assignment_type === 'one_employee' ? (
                <div className={styles.formItem}>
                  <CustomSelect
                    placeholder={t('All employees')}
                    buttonLabel={t('Filter')}
                    items={employees}
                    multipleSelect={false}
                    inputClassName={cn({[styles.error]: errors.assignments })}
                    onChange={data => {
                      setOptions(prev => ({...prev, employees: data}))
                      setErrors(prev => ({ ...prev, assignments: null }))
                    }}
                    width='100%' />
                </div>
              ) : null
            }
            {
              form.assignment_type === 'selected_employees' ? (
                <div className={styles.formItem}>
                  <CustomSelect
                    placeholder={t('All employees')}
                    buttonLabel={t('Filter')}
                    items={employees}
                    inputClassName={cn({[styles.error]: errors.assignments })}
                    onChange={data => {
                      setOptions(prev => ({...prev, employees: data}))
                      setErrors(prev => ({ ...prev, assignments: null }))
                    }}
                    width='100%' />
                </div>
              ) : null
            }
            {
              form.assignment_type === 'place' ? (
                <div className={styles.formItem}>
                  <InputSelect
                    id='assignment_place'
                    placeholder={t('Select place')}
                    options={places}
                    value={form.assignments[0]?.place_id || null}
                    onChange={handleChangeAssignmentTarget}
                    valueKey='id'
                    labelKey='name'
                    error={errors.assignments}
                  />
                </div>
              ) : null
            }
          </div>

          <div className={styles.dateRow}>
            {
              selectedDocumentType.effective_date && canUpload
                ? <div className={styles.formItem}>
                    <Label text={t('Effective date')} htmlFor='effective_date' />
                    <input
                      id='effective_date'
                      type='date'
                      className={cn(styles.dateInput, {[styles.error]: errors.effective_date})}
                      value={form.effective_date || ''}
                      onChange={handleChange('effective_date')}
                    />
                  </div>
                : null
            }
            {
              selectedDocumentType.expiration_date && canUpload
                ? <div className={styles.formItem}>
                    <Label text={t('Expiration date')} htmlFor='expiration_date' />
                    <input
                      id='expiration_date'
                      type='date'
                      className={cn(styles.dateInput, {[styles.error]: errors.expiration_date})}
                      value={form.expiration_date || ''}
                      onChange={handleChange('expiration_date')}
                    />
                  </div>
                : null
            }
          </div>
          {
            selectedDocumentType.require_due_date_for_action
              ? <div className={styles.formItem}>
                  <Label text={t('Due date for employee action')} htmlFor='due_date_for_action' />
                  <input
                    id='due_date_for_action'
                    type='date'
                    className={styles.dateInput}
                    value={form.due_date_for_action || ''}
                    onChange={handleChange('due_date_for_action')}
                  />
                </div>
              : null
          }
          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Employee actions') }</div>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={styles.switchTitle}>{ t('Require acknowledgment') }</div>
                <div className={styles.switchDesc}>{ t('Employee confirms they have read and understood the document') }</div>
              </div>
              <Switch
                onChange={handleToggle('require_acknowledgment')}
                checked={form.require_acknowledgment}
                disabled={selectedDocumentType.ownership === 'employee'}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={styles.switchTitle}>{ t('Track reading time') }</div>
                <div className={styles.switchDesc}>{ t('Record first seen, last seen and time spent reading') }</div>
              </div>
              <Switch
                onChange={handleToggle('track_reading_time')}
                checked={form.track_reading_time}
                disabled={selectedDocumentType.ownership === 'employee'}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={styles.switchTitle}>{ t('Require electronic signature') }</div>
                <div className={styles.switchDesc}>{ t('Qualified signature through a provider') }</div>
              </div>
              <Switch
                onChange={handleToggle('require_signature')}
                checked={!!form.require_signature}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>{ t('Visibility & reminders') }</div>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={cn(styles.switchTitle, { [styles.switchTitleDisabled]: requiresAction })}>{ t('Employee can view this document') }</div>
                {
                  requiresAction ? (
                    <div className={styles.switchDesc}>{ t('Required because the employee must act on this document') }</div>
                  ) : null
                }
              </div>
              <Switch
                onChange={handleToggle('employee_can_view')}
                checked={requiresAction ? true : !!form.employee_can_view}
                disabled={requiresAction || selectedDocumentType.ownership === 'employee'}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
            <div className={styles.switchRow}>
              <div className={styles.switchInfo}>
                <div className={styles.switchTitle}>{ t('Due-date reminder') }</div>
                <div className={styles.switchDesc}>{ t('Remind employees about upcoming due dates and expirations 7, 3, 2, and 1 day in advance, as well as on the due date') }</div>
              </div>
              <Switch
                onChange={handleToggle('reminder')}
                checked={!!form.reminder}
                offColor='#DDE2E5'
                onColor='#0085FF'
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={40}
              />
            </div>
          </div>
          <div className={styles.formItem}>
            <Label text={t('Notes')} htmlFor='notes' />
            <textarea
              id='notes'
              name='notes'
              placeholder={t('Internal note, visible to administrators only')}
              value={form.notes}
              onChange={handleChange('notes')}
              rows={4}
              className={styles.textarea}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            { form.effective_date ? t('Effective {{date}}', { date: form.effective_date }) : null }
          </div>
          <div className={styles.footerActions}>
            <button type="button" className={styles.cancelButton} onClick={handleClose}>
              { t('Cancel') }
            </button>
            <button type="button" className={styles.submitButton} onClick={handleSubmit}>
              { t('Assign document') }
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default DocumentForm
